import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Send, X, Mic, MicOff, Loader2, CheckCircle, XCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const STORAGE_KEY = 'cen_ai_session_id';
const SEEN_KEY_PREFIX = 'cen_ai_seen_'; // localStorage flag per non riaprire ogni volta

// Welcome message + quick replies per ruolo (onboarding)
const ROLE_ONBOARDING = {
  super_admin: {
    welcome: 'Ciao! Sei super admin: hai accesso totale. Posso aiutarti a configurare il portale, creare utenti, o spiegarti dove trovare ogni funzione.',
    quickReplies: ['Da dove inizio?', 'Crea un nuovo utente', 'Spiegami le licenze HACCP', 'Dove sono le scadenze?'],
  },
  admin: {
    welcome: 'Benvenuto admin! Posso guidarti nella creazione di aziende, UL, utenti, e nella gestione delle scadenze.',
    quickReplies: ['Come creo una nuova azienda?', 'Come aggiungo un consulente HACCP?', 'Mostrami le scadenze', 'Come funzionano i template DOCX?'],
  },
  amministratore: {
    welcome: 'Ciao! Sono il tuo assistente. Posso aiutarti con aziende, UL, utenti e gestione documenti.',
    quickReplies: ['Da dove inizio?', 'Come creo un\'azienda?', 'Come genero un documento?', 'Spiegami i ruoli'],
  },
  rspp: {
    welcome: 'Ciao RSPP! Sono qui per aiutarti con DVR, formazione, scadenze e documenti delle Aziende che segui.',
    quickReplies: ['Come genero il DVR?', 'Dove vedo le scadenze attestati?', 'Come carico un test online?', 'Variabili Template DOCX'],
  },
  consulente_haccp: {
    welcome: 'Ciao Consulente HACCP! Ti guido nella compilazione di Manuali, Schede Temperature, Verbali Campioni e Ispezioni.',
    quickReplies: ['Come compilo il Manuale HACCP?', 'Come registro le temperature?', 'Come assegno un campione al lab?', 'Check-list Ispezione 70 punti'],
  },
  azienda: {
    welcome: 'Benvenuto! Da qui puoi vedere i documenti che RSPP e Consulente hanno preparato per la tua attività e tenere d\'occhio le scadenze.',
    quickReplies: ['Dove vedo i miei documenti?', 'Quali sono le mie scadenze?', 'Come scarico gli attestati?', 'Posso modificare qualcosa?'],
  },
  collaborator: {
    welcome: 'Ciao! Posso guidarti nelle attività di supporto sulle UL a te assegnate.',
    quickReplies: ['Da dove inizio?', 'Come compilo i documenti?', 'Mostra scadenze', 'Dove trovo la matrice documenti?'],
  },
  gerente: {
    welcome: 'Ciao Gerente! Da qui controlli la situazione della tua UL: documenti, personale, formazione.',
    quickReplies: ['Quali documenti devo avere?', 'Come vedo le scadenze?', 'Come carico un attestato?', 'Personale dell\'UL'],
  },
  docente: {
    welcome: 'Ciao Docente! Posso aiutarti con materiale didattico, test di formazione e sessioni.',
    quickReplies: ['Come carico un test?', 'Come creo una sessione?', 'Come invio link agli iscritti?', 'Mie fatture'],
  },
  docente_online: {
    welcome: 'Ciao Docente Online! Posso guidarti con aule virtuali, chiavi prepagate, registri presenze e fatture.',
    quickReplies: ['Come funzionano le chiavi?', 'Come creo un\'aula virtuale?', 'Come emetto una fattura?', 'Come pubblico un test online?'],
  },
  medico_competente: {
    welcome: 'Ciao Medico! Posso aiutarti con la programmazione delle visite mediche e la sorveglianza sanitaria.',
    quickReplies: ['Come programmo una visita?', 'Dove vedo lo storico?', 'Scadenze visite imminenti'],
  },
  laboratorio_analisi: {
    welcome: 'Ciao Lab! Da qui ricevi i campioni HACCP da analizzare e carichi i referti PDF.',
    quickReplies: ['Dove vedo i campioni assegnati?', 'Come carico un referto?', 'Stato dei miei lavori'],
  },
};

const DEFAULT_ONBOARDING = {
  welcome: 'Ciao! 👋 Sono il tuo assistente CEN. Posso spiegarti come usare il portale, guidarti nelle pagine giuste e perfino eseguire azioni per te.',
  quickReplies: ['Da dove inizio?', 'Spiegami cosa posso fare', 'Come genero un documento?', 'Variabili Template DOCX'],
};

function getOrCreateSessionId() {
  let id = sessionStorage.getItem(STORAGE_KEY);
  if (!id) {
    id = `cen-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(STORAGE_KEY, id);
  }
  return id;
}

export default function AiAssistant() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const onboarding = (user?.role && ROLE_ONBOARDING[user.role]) || DEFAULT_ONBOARDING;

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: onboarding.welcome },
  ]);
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingActionId, setPendingActionId] = useState(null);
  const recRef = useRef(null);
  const sessionId = useRef(getOrCreateSessionId()).current;
  const listRef = useRef(null);

  // Refresh welcome when role changes (after login)
  useEffect(() => {
    setMessages([{ role: 'assistant', text: onboarding.welcome }]);
    setShowQuickReplies(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  // Auto-apertura alla prima login per ogni utente (one-time)
  useEffect(() => {
    if (!user?.id) return;
    const seenKey = `${SEEN_KEY_PREFIX}${user.id}`;
    let alreadySeen = false;
    try { alreadySeen = !!localStorage.getItem(seenKey); } catch { /* ignore */ }
    if (alreadySeen) return;
    // Apri dopo 1.5s per non disturbare il primo render della dashboard
    const t = setTimeout(() => {
      setOpen(true);
      try { localStorage.setItem(seenKey, '1'); } catch { /* ignore */ }
    }, 1500);
    return () => clearTimeout(t);
  }, [user?.id]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const txt = (text || input).trim();
    if (!txt) return;
    setInput('');
    setShowQuickReplies(false);
    setMessages((p) => [...p, { role: 'user', text: txt }]);
    setLoading(true);
    try {
      const res = await api.post('/ai/chat', { session_id: sessionId, message: txt });
      const d = res.data;
      setMessages((p) => [...p, { role: 'assistant', text: d.text, message_id: d.message_id }]);
      if (d.pending_action) {
        setPendingAction(d.pending_action);
        setPendingActionId(d.message_id);
      }
      if (d.navigate && !d.pending_action) {
        // Naviga dopo 600ms per dare tempo di leggere
        setTimeout(() => { navigate(d.navigate); }, 600);
      }
    } catch (e) {
      setMessages((p) => [...p, { role: 'assistant', text: '⚠ Errore nel contattare l\'assistente.' }]);
    } finally {
      setLoading(false);
    }
  };

  const confirmAction = async (confirmed) => {
    if (!pendingActionId) return;
    try {
      const res = await api.post('/ai/execute-action', { session_id: sessionId, action_id: pendingActionId, confirmed });
      const d = res.data;
      let txt = d.message || (d.executed ? 'Fatto!' : 'Annullato');
      if (d.temp_password) {
        txt += `\n🔑 Password temporanea: ${d.temp_password}`;
      }
      setMessages((p) => [...p, { role: 'assistant', text: txt }]);
      if (d.executed) toast.success(d.message || 'Azione eseguita');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore esecuzione');
    } finally {
      setPendingAction(null);
      setPendingActionId(null);
    }
  };

  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Il tuo browser non supporta il riconoscimento vocale. Usa Chrome/Edge.'); return; }
    if (listening) { recRef.current?.stop(); setListening(false); return; }
    const rec = new SR();
    rec.lang = 'it-IT';
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setInput(transcript);
      setListening(false);
      // Auto-invia
      setTimeout(() => send(transcript), 200);
    };
    rec.onerror = () => { setListening(false); toast.error('Errore microfono'); };
    rec.onend = () => { setListening(false); };
    recRef.current = rec;
    rec.start();
    setListening(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-xl hover:scale-110 transition-transform flex items-center justify-center"
        title="Assistente AI"
        data-testid="ai-assistant-toggle"
      >
        {open ? <X className="w-6 h-6" /> : <Bot className="w-6 h-6" />}
        {/* Badge pulsante quando l'utente non ha ancora interagito */}
        {!open && showQuickReplies && (
          <span className="absolute -top-1 -right-1 flex h-3 w-3" aria-hidden>
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
          </span>
        )}
      </button>
      {open && (
        <div className="fixed bottom-24 right-6 z-40 w-[380px] max-w-[92vw] h-[560px] max-h-[80vh] bg-white rounded-xl shadow-2xl border border-orange-200 flex flex-col" data-testid="ai-assistant-panel">
          <div className="px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-t-xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5" />
              <div>
                <div className="font-semibold">CEN Assistente</div>
                <div className="text-xs opacity-90">Guida per nuovi utenti · Posso eseguire azioni</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="hover:bg-white/10 p-1 rounded"><X className="w-4 h-4" /></button>
          </div>
          <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-orange-500 text-white' : 'bg-white border border-gray-200 text-gray-800'}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {/* Quick replies onboarding (visibile solo all'inizio) */}
            {showQuickReplies && !loading && onboarding.quickReplies?.length > 0 && (
              <div className="pt-1" data-testid="ai-quick-replies">
                <div className="text-[10px] uppercase font-semibold text-gray-400 mb-1.5 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Suggerimenti rapidi
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {onboarding.quickReplies.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => send(q)}
                      className="text-xs px-2.5 py-1.5 rounded-full bg-white border border-orange-200 hover:bg-orange-50 hover:border-orange-400 text-orange-700 transition-colors"
                      data-testid={`ai-quick-reply-${q.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 px-3 py-2 rounded-lg text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Sto pensando...
                </div>
              </div>
            )}
            {pendingAction && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid="ai-pending-action">
                <div className="text-xs font-semibold text-amber-800 mb-2">CONFERMA AZIONE</div>
                <div className="text-xs text-gray-700 mb-3">
                  {pendingAction.type === 'create_user' && (
                    <div>Creo l'utente <strong>{pendingAction.payload?.full_name}</strong> ({pendingAction.payload?.email}) con ruolo <strong>{pendingAction.payload?.role}</strong>?</div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => confirmAction(true)} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs py-2 rounded font-semibold flex items-center justify-center gap-1" data-testid="ai-confirm-yes">
                    <CheckCircle className="w-4 h-4" /> Sì, esegui
                  </button>
                  <button onClick={() => confirmAction(false)} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 text-xs py-2 rounded font-semibold flex items-center justify-center gap-1">
                    <XCircle className="w-4 h-4" /> Annulla
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="p-3 border-t flex gap-2">
            <button onClick={toggleMic} className={`p-2 rounded-lg ${listening ? 'bg-red-500 text-white animate-pulse' : 'bg-gray-200 hover:bg-gray-300'}`} title="Premi per parlare" data-testid="ai-mic-btn">
              {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
              placeholder="Chiedi qualsiasi cosa..."
              className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              data-testid="ai-input"
              disabled={loading}
            />
            <button onClick={() => send()} className="bg-orange-500 hover:bg-orange-600 text-white p-2 rounded-lg" disabled={loading || !input.trim()} data-testid="ai-send-btn">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
